"use client";

import { useEffect, useRef, useState } from "react";

/* ── Site plan model ───────────────────────────────────────────────────────
   Types, styling tables, persistence and geometry for the site plan sketch
   canvas. Everything here is plain data — rendering lives in PlanSvg.tsx,
   interaction in Editor.tsx. Elements persist as JSON in localStorage, so
   every new field must be optional to keep old sketches loading. */

// Canvas coordinate space (viewBox). Rendered responsively.
export const W = 800;
export const H = 560;

export type ZoneType =
  | "exclusion"
  | "building"
  | "hotworks"
  | "laydown"
  | "public"
  | "welfare"
  | "reversing"
  | "washout"
  | "dropzone"
  | "fragile"
  | "mewpzone"
  | "digarea";
export type RouteType = "vehicle" | "pedestrian" | "escape" | "delivery" | "emergency";
export type BarrierType = "boundary" | "barrier" | "walkway" | "service-gas" | "service-water" | "service-electric";
export type ShapeType = "rect" | "ellipse" | "triangle";
export type IconType =
  // Access & traffic
  | "gate"
  | "parking"
  | "banksman"
  | "forklift"
  | "crossing"
  | "speed"
  | "stop"
  | "wheelwash"
  | "holding"
  | "loading"
  // Fire & emergency
  | "fire"
  | "callpoint"
  | "hydrant"
  | "firemarshal"
  | "shutoff"
  | "exit"
  | "assembly"
  | "refuge"
  | "rescue"
  // First aid & welfare
  | "firstaid"
  | "aed"
  | "welfare"
  // Lifting, plant & height
  | "crane"
  | "outrigger"
  | "hoist"
  | "mewp"
  | "scaffold"
  | "anchor"
  | "ladder"
  | "excavation"
  // Services & energy
  | "electric"
  | "overhead"
  | "water"
  | "gas"
  | "generator"
  | "lighting"
  // Domestic & utilities
  | "ecv"
  | "stopcock"
  | "consumer"
  // Storage & environment
  | "storage"
  | "waste"
  | "fuel"
  | "coshh"
  | "spill"
  // Permits & hold points
  | "permit"
  | "permitboard"
  | "dig"
  | "isolation"
  | "holdpoint"
  | "signoff"
  | "approval"
  | "tempworks"
  // Hazards & control
  | "asbestos"
  | "noise"
  | "confined"
  | "cctv"
  | "north";

/** ISO 7010-style sign grammar: warning triangles, safe-condition / fire-equipment squares, circles for the rest. */
export type IconShape = "circle" | "square" | "triangle";

export interface PlanBackground {
  dataUrl: string;
  name: string;
  opacity: number;
}

export interface ZoneEl {
  id: number;
  kind: "zone";
  zoneType: ZoneType;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  rotation?: number;
  color?: string;
}
export interface RouteEl {
  id: number;
  kind: "route";
  routeType: RouteType;
  points: { x: number; y: number }[];
  color?: string;
}
/** Linear feature: boundary/hoarding, barrier, walkway, or a marked service run. */
export interface BarrierEl {
  id: number;
  kind: "barrier";
  barrierType: BarrierType;
  points: { x: number; y: number }[];
}
export interface IconEl {
  id: number;
  kind: "icon";
  icon: IconType;
  x: number;
  y: number;
  /** Crane only — exclusion radius in canvas units. */
  radius?: number;
  /** Scale factor, default 1. */
  size?: number;
  color?: string;
}
export interface LabelEl {
  id: number;
  kind: "label";
  x: number;
  y: number;
  text: string;
  color?: string;
  fontSize?: number;
}
/** Freehand pen or highlighter stroke. */
export interface DrawEl {
  id: number;
  kind: "draw";
  points: { x: number; y: number }[];
  color: string;
  width: number;
  highlight?: boolean;
}
/** Straight line, optionally with an arrow head. */
export interface LineEl {
  id: number;
  kind: "line";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  width: number;
  dash?: boolean;
  arrow?: boolean;
}
/** Plain annotation shape (not a typed zone). */
export interface ShapeEl {
  id: number;
  kind: "shape";
  shape: ShapeType;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  filled?: boolean;
  rotation?: number;
}
/** Inserted image (photo, detail, logo) — independent of the page background. */
export interface ImageEl {
  id: number;
  kind: "image";
  x: number;
  y: number;
  w: number;
  h: number;
  dataUrl: string;
  name: string;
  opacity?: number;
  rotation?: number;
}
/** Measured distance between two points — shows metres once calibrated. */
export interface MeasureEl {
  id: number;
  kind: "measure";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}
/** Scale reference: a drawn line with a known real-world length. One per sketch. */
export interface CalibrationEl {
  id: number;
  kind: "calibration";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  metres: number;
}
/** On-canvas legend box, auto-built from the elements in use. One per sketch. */
export interface LegendEl {
  id: number;
  kind: "legend";
  x: number;
  y: number;
}

export type PlanElement =
  | ZoneEl
  | RouteEl
  | BarrierEl
  | IconEl
  | LabelEl
  | DrawEl
  | LineEl
  | ShapeEl
  | ImageEl
  | MeasureEl
  | CalibrationEl
  | LegendEl;

export type Tool =
  | "select"
  | "pan"
  | "draw"
  | "highlight"
  | "line"
  | "arrow"
  | `shape-${ShapeType}`
  | "measure"
  | "calibrate"
  | "label"
  | `zone-${ZoneType}`
  | `route-${RouteType}`
  | `barrier-${BarrierType}`
  | `icon-${IconType}`
  | `stencil-${string}`;

let nextElId = 1;
export function allocElId(): number {
  return nextElId++;
}
export function bumpElId(els: PlanElement[]) {
  nextElId = Math.max(nextElId, ...els.map((e) => e.id + 1));
}

/* ── Persistence ─────────────────────────────────────────────────────────── */

const PLAN_STORE_PREFIX = "ramsdocs.siteplan.";
const PLAN_BACKGROUND_STORE_PREFIX = "ramsdocs.siteplan.background.";

/** Sketch state persisted on this device per host tool. */
export function useSitePlan(storeKey: string): [PlanElement[], (els: PlanElement[]) => void] {
  const [elements, setElements] = useState<PlanElement[]>([]);
  const loaded = useRef(false);
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<PlanElement[] | null>(null);

  const flush = () => {
    if (pending.current === null) return;
    try {
      window.localStorage.setItem(PLAN_STORE_PREFIX + storeKey, JSON.stringify(pending.current));
    } catch {
      /* storage full — sketch still works, just won't persist */
    }
    pending.current = null;
  };

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(PLAN_STORE_PREFIX + storeKey);
      if (raw) {
        const els = JSON.parse(raw) as PlanElement[];
        if (Array.isArray(els)) {
          setElements(els);
          bumpElId(els);
        }
      }
    } catch {
      /* corrupt store — start blank */
    }
    loaded.current = true;
    // Flush any debounced write if the page is hidden or the hook unmounts.
    const onHide = () => flush();
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      if (writeTimer.current) clearTimeout(writeTimer.current);
      flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey]);

  const update = (els: PlanElement[]) => {
    setElements(els);
    if (!loaded.current) return;
    // Debounce writes — drags fire per pointer-move and sketches can carry
    // embedded images, so serialising on every move would jank.
    pending.current = els;
    if (writeTimer.current) clearTimeout(writeTimer.current);
    writeTimer.current = setTimeout(flush, 300);
  };

  return [elements, update];
}

/** Optional uploaded plan/photo stored locally per tool. */
export function useSitePlanBackground(storeKey: string): [PlanBackground | null, (bg: PlanBackground | null) => void] {
  const [background, setBackground] = useState<PlanBackground | null>(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(PLAN_BACKGROUND_STORE_PREFIX + storeKey);
      if (raw) {
        const parsed = JSON.parse(raw) as PlanBackground;
        if (parsed?.dataUrl && parsed?.name) setBackground(parsed);
      }
    } catch {
      /* corrupt store — continue without background */
    }
  }, [storeKey]);

  const update = (bg: PlanBackground | null) => {
    setBackground(bg);
    try {
      if (bg) window.localStorage.setItem(PLAN_BACKGROUND_STORE_PREFIX + storeKey, JSON.stringify(bg));
      else window.localStorage.removeItem(PLAN_BACKGROUND_STORE_PREFIX + storeKey);
    } catch {
      /* storage full — sketch still works, just won't persist */
    }
  };

  return [background, update];
}

/* ── Styling tables ──────────────────────────────────────────────────────── */

export const ROUTE_STYLE: Record<RouteType, { stroke: string; dash?: string; label: string }> = {
  vehicle: { stroke: "#f2772e", label: "Vehicle route" },
  pedestrian: { stroke: "#2563eb", dash: "8 6", label: "Pedestrian route" },
  escape: { stroke: "#1ba37b", label: "Escape route" },
  delivery: { stroke: "#8a6400", dash: "12 5", label: "Delivery route" },
  emergency: { stroke: "#dc2626", dash: "4 5", label: "Emergency access route" },
};

export const ZONE_STYLE: Record<ZoneType, { stroke: string; fill: string; label: string }> = {
  exclusion: { stroke: "#dc2626", fill: "rgba(220,38,38,0.12)", label: "Exclusion zone" },
  building: { stroke: "#475569", fill: "rgba(71,85,105,0.15)", label: "Building / structure" },
  hotworks: { stroke: "#b34e10", fill: "rgba(242,119,46,0.16)", label: "Hot works area" },
  laydown: { stroke: "#8a6400", fill: "rgba(138,100,0,0.13)", label: "Storage / laydown zone" },
  public: { stroke: "#7c3aed", fill: "rgba(124,58,237,0.1)", label: "Public interface" },
  welfare: { stroke: "#2563eb", fill: "rgba(37,99,235,0.1)", label: "Welfare / site facilities" },
  reversing: { stroke: "#9a3412", fill: "rgba(154,52,18,0.12)", label: "Reversing / turning area" },
  washout: { stroke: "#0e7a5c", fill: "rgba(14,122,92,0.12)", label: "Concrete washout / containment" },
  dropzone: { stroke: "#b91c1c", fill: "rgba(185,28,28,0.1)", label: "Drop zone — falling objects" },
  fragile: { stroke: "#a16207", fill: "rgba(161,98,7,0.12)", label: "Fragile roof area" },
  mewpzone: { stroke: "#0369a1", fill: "rgba(3,105,161,0.1)", label: "MEWP / plant operating area" },
  digarea: { stroke: "#92400e", fill: "rgba(146,64,14,0.12)", label: "Permit-to-dig area" },
};

export const BARRIER_STYLE: Record<
  BarrierType,
  { stroke: string; width: number; dash?: string; deco: "posts" | "ticks" | "double" | "letter"; letter?: string; label: string }
> = {
  boundary: { stroke: "#0f172a", width: 2.5, deco: "posts", label: "Site boundary / hoarding" },
  barrier: { stroke: "#f2772e", width: 2.5, deco: "ticks", label: "Pedestrian barrier / segregation" },
  walkway: { stroke: "#475569", width: 10, deco: "double", label: "Covered / protected walkway" },
  "service-gas": { stroke: "#ca8a04", width: 2.5, dash: "10 6", deco: "letter", letter: "G", label: "Gas service run" },
  "service-water": { stroke: "#0284c7", width: 2.5, dash: "10 6", deco: "letter", letter: "W", label: "Water service run" },
  "service-electric": { stroke: "#dc2626", width: 2.5, dash: "10 6", deco: "letter", letter: "E", label: "Electric service run" },
};

// Warning triangles share the ISO yellow; squares split green (safe condition) / red (fire equipment).
const TRI = { shape: "triangle" as IconShape, bg: "#f6c100", fg: "#111827" };
const FIRE_SQ = { shape: "square" as IconShape, bg: "#dc2626" };
const SAFE_SQ = { shape: "square" as IconShape, bg: "#1ba37b" };

export const ICON_META: Record<IconType, { glyph: string; bg: string; label: string; shape?: IconShape; fg?: string; ring?: string }> = {
  // Access & traffic
  gate: { glyph: "G", bg: "#0f172a", label: "Site gate / access" },
  parking: { glyph: "P", bg: "#475569", label: "Parking / vehicle holding" },
  banksman: { glyph: "B", bg: "#f2772e", label: "Banksman / traffic marshal" },
  forklift: { glyph: "FL", bg: "#f2772e", label: "Forklift / telehandler crossing" },
  crossing: { glyph: "PX", bg: "#2563eb", label: "Pedestrian crossing point" },
  speed: { glyph: "10", bg: "#ffffff", fg: "#111827", ring: "#dc2626", label: "Site speed limit" },
  stop: { glyph: "ST", bg: "#dc2626", label: "Stop / give way" },
  wheelwash: { glyph: "WW", bg: "#0284c7", label: "Wheel wash" },
  holding: { glyph: "DH", bg: "#8a6400", label: "Delivery holding bay" },
  loading: { glyph: "LB", bg: "#8a6400", label: "Loading / unloading bay" },
  // Fire & emergency
  fire: { glyph: "F", ...FIRE_SQ, label: "Fire point / extinguisher" },
  callpoint: { glyph: "FA", ...FIRE_SQ, label: "Fire alarm call point" },
  hydrant: { glyph: "H", ...FIRE_SQ, label: "Hydrant / dry riser" },
  firemarshal: { glyph: "FM", ...FIRE_SQ, label: "Fire marshal station" },
  shutoff: { glyph: "ES", ...FIRE_SQ, label: "Emergency shut-off" },
  exit: { glyph: "EX", ...SAFE_SQ, label: "Fire exit" },
  assembly: { glyph: "A", ...SAFE_SQ, label: "Assembly / muster point" },
  refuge: { glyph: "RF", ...SAFE_SQ, label: "Refuge point" },
  rescue: { glyph: "RK", ...SAFE_SQ, label: "Rescue kit / stretcher point" },
  // First aid & welfare
  firstaid: { glyph: "+", ...SAFE_SQ, label: "First aid" },
  aed: { glyph: "D", ...SAFE_SQ, label: "Defibrillator (AED)" },
  welfare: { glyph: "W", bg: "#2563eb", label: "Welfare unit" },
  // Lifting, plant & height
  crane: { glyph: "C", bg: "#0f172a", label: "Crane (with exclusion radius)" },
  outrigger: { glyph: "OM", bg: "#0f172a", label: "Outrigger / mat position" },
  hoist: { glyph: "HO", bg: "#475569", label: "Hoist / loading platform" },
  mewp: { glyph: "M", bg: "#f2772e", label: "MEWP / access tower" },
  scaffold: { glyph: "SC", bg: "#475569", label: "Scaffold / work at height" },
  anchor: { glyph: "AN", bg: "#2563eb", label: "Harness anchor / rescue point" },
  ladder: { glyph: "LA", bg: "#475569", label: "Ladder access point" },
  excavation: { glyph: "X", ...TRI, label: "Excavation / open trench" },
  // Services & energy
  electric: { glyph: "E", ...TRI, label: "Electrical isolation / live service" },
  overhead: { glyph: "O", ...TRI, label: "Overhead service / height restriction" },
  water: { glyph: "W", ...TRI, label: "Water / underground service" },
  gas: { glyph: "GS", ...TRI, label: "Gas / cylinder storage" },
  generator: { glyph: "GN", bg: "#7c3aed", label: "Generator / temporary power" },
  lighting: { glyph: "LT", bg: "#8a6400", label: "Lighting tower" },
  // Domestic & utilities
  ecv: { glyph: "EV", bg: "#ca8a04", label: "Gas emergency control valve (ECV)" },
  stopcock: { glyph: "SV", bg: "#0284c7", label: "Water stopcock / isolation valve" },
  consumer: { glyph: "CU", bg: "#7c3aed", label: "Consumer unit / electrical isolator" },
  // Storage & environment
  storage: { glyph: "S", bg: "#8a6400", label: "Storage / laydown" },
  waste: { glyph: "WS", bg: "#8a6400", label: "Waste / skip" },
  fuel: { glyph: "FU", ...TRI, label: "Fuel store" },
  coshh: { glyph: "CH", bg: "#b34e10", label: "COSHH store / cabinet" },
  spill: { glyph: "!", ...SAFE_SQ, label: "Spill kit / environmental control" },
  // Permits & hold points
  permit: { glyph: "P", bg: "#0e7a5c", label: "Permit-controlled work point" },
  permitboard: { glyph: "PB", bg: "#0e7a5c", label: "Permit board / sign-in" },
  dig: { glyph: "PD", bg: "#92400e", label: "Permit to dig / service scan point" },
  isolation: { glyph: "IS", bg: "#7c3aed", label: "Isolation permit point" },
  holdpoint: { glyph: "HP", bg: "#b34e10", label: "Hold point — stop & inspect" },
  signoff: { glyph: "SO", bg: "#0f172a", label: "Supervisor sign-off point" },
  approval: { glyph: "AP", bg: "#0f172a", label: "Client / PC approval required" },
  tempworks: { glyph: "TW", bg: "#475569", label: "Temporary works check" },
  // Hazards & control
  asbestos: { glyph: "AB", ...TRI, label: "Asbestos / hazardous material" },
  noise: { glyph: "NZ", ...TRI, label: "Noise / vibration zone" },
  confined: { glyph: "CF", ...TRI, label: "Confined space entry" },
  cctv: { glyph: "CC", bg: "#475569", label: "CCTV / security" },
  north: { glyph: "N", bg: "#475569", label: "North" },
};

/** Marker palette grouping — drives the searchable marker panel. */
export const ICON_CATEGORIES: { heading: string; icons: IconType[] }[] = [
  { heading: "Access & traffic", icons: ["gate", "parking", "banksman", "forklift", "crossing", "speed", "stop", "wheelwash", "holding", "loading"] },
  { heading: "Fire & emergency", icons: ["fire", "callpoint", "hydrant", "firemarshal", "shutoff", "exit", "assembly", "refuge", "rescue"] },
  { heading: "First aid & welfare", icons: ["firstaid", "aed", "welfare"] },
  { heading: "Lifting, plant & height", icons: ["crane", "outrigger", "hoist", "mewp", "scaffold", "anchor", "ladder", "excavation"] },
  { heading: "Services & energy", icons: ["electric", "overhead", "water", "gas", "generator", "lighting"] },
  { heading: "Domestic & utilities", icons: ["ecv", "stopcock", "consumer"] },
  { heading: "Storage & environment", icons: ["storage", "waste", "fuel", "coshh", "spill"] },
  { heading: "Permits & hold points", icons: ["permit", "permitboard", "dig", "isolation", "holdpoint", "signoff", "approval", "tempworks"] },
  { heading: "Hazards & control", icons: ["asbestos", "noise", "confined", "cctv", "north"] },
];

/* ── Layer presets ───────────────────────────────────────────────────────── */

/** One-click plan presets — filter the toolbar and palette to the layers that matter for a plan type. */
export interface PlanPreset {
  id: string;
  label: string;
  zones: ZoneType[];
  routes: RouteType[];
  barriers: BarrierType[];
  icons: IconType[];
}

export const PLAN_PRESETS: PlanPreset[] = [
  {
    id: "traffic",
    label: "Traffic plan",
    zones: ["reversing", "laydown", "public", "exclusion"],
    routes: ["vehicle", "pedestrian", "delivery", "emergency"],
    barriers: ["boundary", "barrier", "walkway"],
    icons: ["gate", "banksman", "crossing", "speed", "stop", "parking", "holding", "loading", "wheelwash", "forklift", "north"],
  },
  {
    id: "fire",
    label: "Fire / emergency plan",
    zones: ["welfare", "building", "laydown"],
    routes: ["escape", "emergency", "pedestrian"],
    barriers: ["boundary", "walkway"],
    icons: ["fire", "callpoint", "hydrant", "exit", "assembly", "refuge", "firemarshal", "shutoff", "rescue", "aed", "firstaid", "gas", "fuel", "north"],
  },
  {
    id: "lift",
    label: "Lift plan",
    zones: ["exclusion", "laydown", "dropzone", "reversing", "public"],
    routes: ["vehicle", "pedestrian", "emergency"],
    barriers: ["boundary", "barrier"],
    icons: ["crane", "outrigger", "hoist", "banksman", "loading", "anchor", "overhead", "holdpoint", "signoff", "permit", "north"],
  },
  {
    id: "excavation",
    label: "Excavation plan",
    zones: ["digarea", "exclusion", "laydown"],
    routes: ["vehicle", "pedestrian"],
    barriers: ["boundary", "barrier", "service-gas", "service-water", "service-electric"],
    icons: ["dig", "permit", "excavation", "ladder", "water", "gas", "electric", "overhead", "confined", "holdpoint", "signoff", "north"],
  },
  {
    id: "hotworks",
    label: "Hot works plan",
    zones: ["hotworks", "exclusion", "welfare"],
    routes: ["escape", "pedestrian"],
    barriers: ["barrier"],
    icons: ["fire", "callpoint", "assembly", "firstaid", "firemarshal", "shutoff", "permit", "permitboard", "holdpoint", "gas", "fuel", "spill", "north"],
  },
  {
    id: "height",
    label: "Work at height",
    zones: ["dropzone", "fragile", "exclusion", "mewpzone"],
    routes: ["pedestrian", "escape"],
    barriers: ["barrier", "walkway"],
    icons: ["scaffold", "mewp", "ladder", "anchor", "hoist", "loading", "rescue", "holdpoint", "tempworks", "north"],
  },
];

/* ── Composite RAMS stencils ─────────────────────────────────────────────── */

type StencilPoint = { x: number; y: number };

export type CompositeStencilElement =
  | {
      kind: "zone";
      zoneType: ZoneType;
      x: number;
      y: number;
      w: number;
      h: number;
      label?: string;
      color?: string;
    }
  | {
      kind: "route";
      routeType: RouteType;
      points: StencilPoint[];
      color?: string;
    }
  | {
      kind: "barrier";
      barrierType: BarrierType;
      points: StencilPoint[];
    }
  | {
      kind: "icon";
      icon: IconType;
      x: number;
      y: number;
      radius?: number;
      size?: number;
      color?: string;
    }
  | {
      kind: "label";
      x: number;
      y: number;
      text: string;
      color?: string;
      fontSize?: number;
    };

export interface CompositeStencil {
  id: string;
  label: string;
  category: string;
  summary: string;
  tags: string[];
  elements: CompositeStencilElement[];
}

export const COMPOSITE_STENCIL_GROUPS: { heading: string; stencils: CompositeStencil[] }[] = [
  {
    heading: "Site set-up & briefings",
    stencils: [
      {
        id: "site-setup-briefing",
        label: "Site set-up briefing pack",
        category: "Site set-up & briefings",
        summary: "Gate, welfare, first aid, fire point, permit board and briefing/sign-off point.",
        tags: ["site setup", "induction", "welfare", "briefing", "permit board", "sign off"],
        elements: [
          { kind: "zone", zoneType: "welfare", x: -110, y: -48, w: 96, h: 60, label: "Welfare / induction" },
          { kind: "icon", icon: "gate", x: -142, y: 58 },
          { kind: "icon", icon: "welfare", x: -62, y: -18 },
          { kind: "icon", icon: "firstaid", x: 10, y: -22 },
          { kind: "icon", icon: "fire", x: 58, y: -22 },
          { kind: "icon", icon: "permitboard", x: 108, y: -22 },
          { kind: "icon", icon: "signoff", x: 108, y: 38 },
          { kind: "label", x: -148, y: 88, text: "Briefing / sign-in / site rules point", fontSize: 13 },
        ],
      },
      {
        id: "rams-hold-point",
        label: "RAMS hold-point pack",
        category: "Site set-up & briefings",
        summary: "Stop, inspect, supervisor sign-off and PC/client approval marker set.",
        tags: ["hold point", "inspection", "supervisor", "approval", "sign off", "RAMS review"],
        elements: [
          { kind: "icon", icon: "holdpoint", x: -54, y: 0 },
          { kind: "icon", icon: "signoff", x: 0, y: 0 },
          { kind: "icon", icon: "approval", x: 54, y: 0 },
          { kind: "label", x: -92, y: 34, text: "Hold point - stop, inspect and sign off", fontSize: 13 },
        ],
      },
      {
        id: "temporary-works-check",
        label: "Temporary works check pack",
        category: "Site set-up & briefings",
        summary: "Temporary works check, exclusion marker and formal sign-off point.",
        tags: ["temporary works", "inspection", "scaffold", "support", "sign off"],
        elements: [
          { kind: "zone", zoneType: "exclusion", x: -88, y: -44, w: 176, h: 88, label: "TW check area" },
          { kind: "icon", icon: "tempworks", x: -42, y: 0 },
          { kind: "icon", icon: "holdpoint", x: 14, y: 0 },
          { kind: "icon", icon: "signoff", x: 70, y: 0 },
          { kind: "label", x: -82, y: 66, text: "Temporary works check before use", fontSize: 13 },
        ],
      },
    ],
  },
  {
    heading: "Traffic management",
    stencils: [
      {
        id: "traffic-segregation",
        label: "Vehicle/pedestrian segregation",
        category: "Traffic management",
        summary: "Vehicle route, protected pedestrian route, crossing, barriers, banksman and speed/stop controls.",
        tags: ["traffic", "pedestrian", "vehicle", "segregation", "crossing", "banksman"],
        elements: [
          { kind: "route", routeType: "vehicle", points: [{ x: -180, y: -52 }, { x: -40, y: -52 }, { x: 160, y: -22 }] },
          { kind: "route", routeType: "pedestrian", points: [{ x: -170, y: 58 }, { x: -26, y: 58 }, { x: 160, y: 44 }] },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -176, y: 14 }, { x: 168, y: 4 }] },
          { kind: "icon", icon: "gate", x: -188, y: -52 },
          { kind: "icon", icon: "banksman", x: -32, y: -18 },
          { kind: "icon", icon: "crossing", x: -20, y: 36 },
          { kind: "icon", icon: "speed", x: 76, y: -28 },
          { kind: "icon", icon: "stop", x: 140, y: -2 },
          { kind: "label", x: -178, y: 92, text: "Segregated routes - keep crossing controlled", fontSize: 13 },
        ],
      },
      {
        id: "delivery-holding-loading",
        label: "Delivery holding/loading bay",
        category: "Traffic management",
        summary: "Delivery route, holding bay, loading bay, banksman and stop/gate controls.",
        tags: ["delivery", "holding bay", "loading bay", "unloading", "banksman", "traffic"],
        elements: [
          { kind: "zone", zoneType: "reversing", x: -138, y: -74, w: 118, h: 74, label: "Delivery holding" },
          { kind: "zone", zoneType: "laydown", x: 30, y: -72, w: 132, h: 84, label: "Loading / unloading" },
          { kind: "route", routeType: "delivery", points: [{ x: -200, y: 40 }, { x: -98, y: -36 }, { x: 42, y: -24 }, { x: 166, y: -20 }] },
          { kind: "icon", icon: "gate", x: -204, y: 40 },
          { kind: "icon", icon: "holding", x: -84, y: -36 },
          { kind: "icon", icon: "loading", x: 92, y: -30 },
          { kind: "icon", icon: "banksman", x: 10, y: 24 },
          { kind: "icon", icon: "stop", x: -142, y: 12 },
          { kind: "label", x: -160, y: 80, text: "Book deliveries - marshal reversing and unloading", fontSize: 13 },
        ],
      },
      {
        id: "reversing-control",
        label: "Reversing/turning control",
        category: "Traffic management",
        summary: "Reversing zone with banksman, stop control, exclusion barrier and warning label.",
        tags: ["reversing", "turning", "banksman", "plant", "segregation"],
        elements: [
          { kind: "zone", zoneType: "reversing", x: -96, y: -58, w: 192, h: 116, label: "Reversing / turning only" },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -116, y: 76 }, { x: 116, y: 76 }] },
          { kind: "icon", icon: "banksman", x: -72, y: 0 },
          { kind: "icon", icon: "stop", x: 0, y: 0 },
          { kind: "icon", icon: "forklift", x: 72, y: 0 },
          { kind: "label", x: -108, y: 104, text: "Controlled reversing - no pedestrians", fontSize: 13 },
        ],
      },
      {
        id: "one-way-site-traffic",
        label: "One-way site traffic loop",
        category: "Traffic management",
        summary: "Primary vehicle loop with entry/exit gate, speed limit, stop point and pedestrian crossing.",
        tags: ["one way", "traffic loop", "vehicle route", "site access", "crossing"],
        elements: [
          { kind: "route", routeType: "vehicle", points: [{ x: -166, y: -56 }, { x: 142, y: -56 }, { x: 142, y: 58 }, { x: -166, y: 58 }] },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -166, y: 10 }, { x: 142, y: 10 }] },
          { kind: "route", routeType: "pedestrian", points: [{ x: -142, y: 92 }, { x: 0, y: 92 }, { x: 142, y: 92 }] },
          { kind: "icon", icon: "gate", x: -174, y: -56 },
          { kind: "icon", icon: "speed", x: 28, y: -56 },
          { kind: "icon", icon: "stop", x: 142, y: -8 },
          { kind: "icon", icon: "crossing", x: 0, y: 44 },
          { kind: "label", x: -160, y: 124, text: "One-way site traffic - crossing controlled", fontSize: 13 },
        ],
      },
      {
        id: "emergency-access-keep-clear",
        label: "Emergency access keep-clear",
        category: "Traffic management",
        summary: "Emergency vehicle route with keep-clear barrier and rescue/fire markers.",
        tags: ["emergency access", "keep clear", "fire route", "rescue", "vehicle route"],
        elements: [
          { kind: "route", routeType: "emergency", points: [{ x: -190, y: 0 }, { x: -40, y: 0 }, { x: 170, y: -34 }] },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -182, y: 34 }, { x: 174, y: 8 }] },
          { kind: "icon", icon: "gate", x: -194, y: 0 },
          { kind: "icon", icon: "fire", x: -20, y: -22 },
          { kind: "icon", icon: "rescue", x: 62, y: -30 },
          { kind: "label", x: -154, y: 66, text: "Emergency access - keep clear at all times", fontSize: 13 },
        ],
      },
    ],
  },
  {
    heading: "Fire, emergency & hot works",
    stencils: [
      {
        id: "fire-evacuation",
        label: "Fire evacuation pack",
        category: "Fire, emergency & hot works",
        summary: "Escape route, exit, assembly point, call point, fire point and first aid.",
        tags: ["fire", "evacuation", "escape route", "assembly", "first aid"],
        elements: [
          { kind: "route", routeType: "escape", points: [{ x: -172, y: 38 }, { x: -40, y: 10 }, { x: 130, y: -32 }] },
          { kind: "icon", icon: "exit", x: -174, y: 38 },
          { kind: "icon", icon: "assembly", x: 142, y: -34 },
          { kind: "icon", icon: "callpoint", x: -72, y: -20 },
          { kind: "icon", icon: "fire", x: -20, y: -22 },
          { kind: "icon", icon: "firstaid", x: 34, y: -22 },
          { kind: "label", x: -136, y: 70, text: "Escape route to assembly point", fontSize: 13 },
        ],
      },
      {
        id: "hot-works-permit",
        label: "Hot works permit pack",
        category: "Fire, emergency & hot works",
        summary: "Hot works zone, permit point, fire watch, extinguishers, fuel/gas and spill control.",
        tags: ["hot works", "permit", "fire watch", "extinguisher", "gas", "fuel"],
        elements: [
          { kind: "zone", zoneType: "hotworks", x: -100, y: -62, w: 200, h: 116, label: "Hot works area" },
          { kind: "zone", zoneType: "exclusion", x: -124, y: -82, w: 248, h: 156, label: "No combustibles / controlled access" },
          { kind: "icon", icon: "permit", x: -74, y: -28 },
          { kind: "icon", icon: "fire", x: 0, y: -34 },
          { kind: "icon", icon: "firemarshal", x: 70, y: -28 },
          { kind: "icon", icon: "gas", x: -52, y: 32 },
          { kind: "icon", icon: "fuel", x: 18, y: 34 },
          { kind: "icon", icon: "spill", x: 84, y: 32 },
          { kind: "label", x: -112, y: 98, text: "Hot works permit - fire watch and clear combustibles", fontSize: 13 },
        ],
      },
      {
        id: "fire-point-station",
        label: "Fire point station",
        category: "Fire, emergency & hot works",
        summary: "Fire point with alarm call point, marshal station and shut-off marker.",
        tags: ["fire point", "extinguisher", "alarm", "fire marshal", "shut off"],
        elements: [
          { kind: "icon", icon: "fire", x: -72, y: 0 },
          { kind: "icon", icon: "callpoint", x: -24, y: 0 },
          { kind: "icon", icon: "firemarshal", x: 24, y: 0 },
          { kind: "icon", icon: "shutoff", x: 72, y: 0 },
          { kind: "label", x: -92, y: 36, text: "Fire point / alarm / emergency shut-off", fontSize: 13 },
        ],
      },
      {
        id: "first-aid-rescue",
        label: "First aid and rescue point",
        category: "Fire, emergency & hot works",
        summary: "First aid, AED, rescue kit, refuge point and route to the work area.",
        tags: ["first aid", "AED", "rescue", "refuge", "emergency"],
        elements: [
          { kind: "route", routeType: "emergency", points: [{ x: -130, y: 46 }, { x: -20, y: 20 }, { x: 112, y: 0 }] },
          { kind: "icon", icon: "firstaid", x: -70, y: 0 },
          { kind: "icon", icon: "aed", x: -22, y: 0 },
          { kind: "icon", icon: "rescue", x: 28, y: 0 },
          { kind: "icon", icon: "refuge", x: 78, y: 0 },
          { kind: "label", x: -106, y: 34, text: "First aid / AED / rescue equipment", fontSize: 13 },
        ],
      },
    ],
  },
  {
    heading: "Lifting, plant & height",
    stencils: [
      {
        id: "crane-lift",
        label: "Crane lift pack",
        category: "Lifting, plant & height",
        summary: "Crane with radius, exclusion zone, outriggers, laydown area, banksman and sign-off points.",
        tags: ["crane", "lifting", "outriggers", "exclusion", "laydown", "signaller"],
        elements: [
          { kind: "zone", zoneType: "exclusion", x: -128, y: -118, w: 256, h: 236, label: "Lifting exclusion zone" },
          { kind: "zone", zoneType: "laydown", x: 146, y: -54, w: 106, h: 90, label: "Load landing / laydown" },
          { kind: "icon", icon: "crane", x: 0, y: 0, radius: 108 },
          { kind: "icon", icon: "outrigger", x: -48, y: -42, size: 0.82 },
          { kind: "icon", icon: "outrigger", x: 48, y: -42, size: 0.82 },
          { kind: "icon", icon: "outrigger", x: -48, y: 42, size: 0.82 },
          { kind: "icon", icon: "outrigger", x: 48, y: 42, size: 0.82 },
          { kind: "icon", icon: "banksman", x: -112, y: 92 },
          { kind: "icon", icon: "holdpoint", x: 144, y: 68 },
          { kind: "icon", icon: "signoff", x: 198, y: 68 },
          { kind: "label", x: -118, y: 142, text: "Lift plan - exclusion, outrigger mats and signaller shown", fontSize: 13 },
        ],
      },
      {
        id: "mewp-work-area",
        label: "MEWP operating area",
        category: "Lifting, plant & height",
        summary: "MEWP zone, pedestrian segregation, rescue point and hold-point marker.",
        tags: ["MEWP", "access", "rescue", "exclusion", "pedestrian segregation"],
        elements: [
          { kind: "zone", zoneType: "mewpzone", x: -108, y: -62, w: 216, h: 124, label: "MEWP operating area" },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -128, y: 82 }, { x: 128, y: 82 }] },
          { kind: "route", routeType: "pedestrian", points: [{ x: -154, y: 114 }, { x: 154, y: 114 }] },
          { kind: "icon", icon: "mewp", x: -36, y: 0 },
          { kind: "icon", icon: "anchor", x: 30, y: -14 },
          { kind: "icon", icon: "rescue", x: 92, y: 0 },
          { kind: "icon", icon: "holdpoint", x: -94, y: -38 },
          { kind: "label", x: -120, y: 146, text: "MEWP rescue plan and segregated route", fontSize: 13 },
        ],
      },
      {
        id: "scaffold-drop-zone",
        label: "Scaffold/drop-zone pack",
        category: "Lifting, plant & height",
        summary: "Scaffold position, falling-object drop zone, ladder access, hoist/loading point and rescue marker.",
        tags: ["scaffold", "drop zone", "ladder", "hoist", "falling objects"],
        elements: [
          { kind: "zone", zoneType: "dropzone", x: -120, y: -68, w: 240, h: 136, label: "Drop zone - no unauthorised access" },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -134, y: 82 }, { x: 134, y: 82 }] },
          { kind: "icon", icon: "scaffold", x: -72, y: -12 },
          { kind: "icon", icon: "ladder", x: -18, y: -12 },
          { kind: "icon", icon: "hoist", x: 38, y: -12 },
          { kind: "icon", icon: "rescue", x: 92, y: -12 },
          { kind: "icon", icon: "tempworks", x: -94, y: 42 },
          { kind: "label", x: -120, y: 112, text: "Work at height - drop zone and rescue access", fontSize: 13 },
        ],
      },
      {
        id: "fragile-roof-access",
        label: "Fragile roof/access pack",
        category: "Lifting, plant & height",
        summary: "Fragile roof area, access point, anchor/rescue controls, permit and drop zone.",
        tags: ["fragile roof", "roof access", "anchor", "rescue", "permit", "drop zone"],
        elements: [
          { kind: "zone", zoneType: "fragile", x: -118, y: -70, w: 236, h: 112, label: "Fragile roof / fragile surface" },
          { kind: "zone", zoneType: "dropzone", x: -118, y: 58, w: 236, h: 66, label: "Drop zone below" },
          { kind: "icon", icon: "ladder", x: -90, y: -38 },
          { kind: "icon", icon: "anchor", x: -18, y: -38 },
          { kind: "icon", icon: "rescue", x: 52, y: -38 },
          { kind: "icon", icon: "permit", x: 94, y: 78 },
          { kind: "label", x: -110, y: 150, text: "Roof access - fragile area, rescue and drop zone", fontSize: 13 },
        ],
      },
    ],
  },
  {
    heading: "Excavation, services & isolation",
    stencils: [
      {
        id: "permit-to-dig-services",
        label: "Permit-to-dig/services pack",
        category: "Excavation, services & isolation",
        summary: "Dig area, service runs, permit point, excavation marker, access ladder and hold/sign-off points.",
        tags: ["permit to dig", "excavation", "services", "CAT scan", "trial hole", "hold point"],
        elements: [
          { kind: "zone", zoneType: "digarea", x: -126, y: -72, w: 252, h: 144, label: "Permit-to-dig area" },
          { kind: "barrier", barrierType: "service-gas", points: [{ x: -140, y: -34 }, { x: 138, y: -10 }] },
          { kind: "barrier", barrierType: "service-electric", points: [{ x: -132, y: 10 }, { x: 132, y: 34 }] },
          { kind: "barrier", barrierType: "service-water", points: [{ x: -118, y: 52 }, { x: 118, y: 52 }] },
          { kind: "icon", icon: "dig", x: -86, y: -44 },
          { kind: "icon", icon: "excavation", x: -28, y: -6 },
          { kind: "icon", icon: "ladder", x: 40, y: 18 },
          { kind: "icon", icon: "holdpoint", x: 88, y: -44 },
          { kind: "icon", icon: "signoff", x: 88, y: 22 },
          { kind: "label", x: -132, y: 104, text: "Scan/permit before breaking ground - services shown", fontSize: 13 },
        ],
      },
      {
        id: "live-services-isolation",
        label: "Live services/isolation pack",
        category: "Excavation, services & isolation",
        summary: "Gas, water and electric service runs with isolation/permit/hold-point markers.",
        tags: ["live services", "isolation", "gas", "water", "electric", "lock off"],
        elements: [
          { kind: "barrier", barrierType: "service-gas", points: [{ x: -160, y: -48 }, { x: 160, y: -48 }] },
          { kind: "barrier", barrierType: "service-water", points: [{ x: -160, y: 0 }, { x: 160, y: 0 }] },
          { kind: "barrier", barrierType: "service-electric", points: [{ x: -160, y: 48 }, { x: 160, y: 48 }] },
          { kind: "icon", icon: "gas", x: -182, y: -48 },
          { kind: "icon", icon: "water", x: -182, y: 0 },
          { kind: "icon", icon: "electric", x: -182, y: 48 },
          { kind: "icon", icon: "isolation", x: 84, y: -2 },
          { kind: "icon", icon: "permit", x: 136, y: -2 },
          { kind: "icon", icon: "holdpoint", x: 188, y: -2 },
          { kind: "label", x: -156, y: 86, text: "Live services - isolate/permit before intrusive work", fontSize: 13 },
        ],
      },
      {
        id: "overhead-line-goalposts",
        label: "Overhead line goalposts",
        category: "Excavation, services & isolation",
        summary: "Overhead service corridor with goalpost/barrier controls and hold point.",
        tags: ["overhead lines", "goalposts", "height restriction", "plant", "services"],
        elements: [
          { kind: "zone", zoneType: "exclusion", x: -150, y: -52, w: 300, h: 104, label: "Overhead line exclusion" },
          { kind: "barrier", barrierType: "service-electric", points: [{ x: -150, y: 0 }, { x: 150, y: 0 }] },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -118, y: -72 }, { x: -118, y: 72 }] },
          { kind: "barrier", barrierType: "barrier", points: [{ x: 118, y: -72 }, { x: 118, y: 72 }] },
          { kind: "icon", icon: "overhead", x: -38, y: -24 },
          { kind: "icon", icon: "overhead", x: 38, y: 24 },
          { kind: "icon", icon: "holdpoint", x: 0, y: 72 },
          { kind: "label", x: -120, y: 104, text: "Goalposts / height restriction - plant route controlled", fontSize: 13 },
        ],
      },
      {
        id: "confined-space-entry",
        label: "Confined space entry pack",
        category: "Excavation, services & isolation",
        summary: "Confined-space entry with permit, standby/rescue, first aid and emergency route.",
        tags: ["confined space", "permit", "rescue", "standby", "first aid", "emergency"],
        elements: [
          { kind: "zone", zoneType: "exclusion", x: -86, y: -58, w: 172, h: 116, label: "Controlled entry area" },
          { kind: "route", routeType: "emergency", points: [{ x: -130, y: 78 }, { x: -40, y: 42 }, { x: 82, y: 18 }] },
          { kind: "icon", icon: "confined", x: -42, y: -16 },
          { kind: "icon", icon: "permit", x: 18, y: -18 },
          { kind: "icon", icon: "rescue", x: 70, y: 18 },
          { kind: "icon", icon: "firstaid", x: -72, y: 42 },
          { kind: "icon", icon: "holdpoint", x: 60, y: -42 },
          { kind: "label", x: -104, y: 104, text: "Confined space - permit, standby and rescue ready", fontSize: 13 },
        ],
      },
      {
        id: "utility-isolation-domestic",
        label: "Domestic utility isolation",
        category: "Excavation, services & isolation",
        summary: "Gas ECV, water stopcock, consumer unit and isolation permit/sign-off points.",
        tags: ["domestic", "utility", "gas", "water", "consumer unit", "isolation"],
        elements: [
          { kind: "icon", icon: "ecv", x: -92, y: -18 },
          { kind: "icon", icon: "stopcock", x: -36, y: -18 },
          { kind: "icon", icon: "consumer", x: 20, y: -18 },
          { kind: "icon", icon: "isolation", x: 78, y: -18 },
          { kind: "icon", icon: "signoff", x: 134, y: -18 },
          { kind: "label", x: -128, y: 24, text: "Isolation confirmed before work starts", fontSize: 13 },
        ],
      },
    ],
  },
  {
    heading: "COSHH, asbestos & environment",
    stencils: [
      {
        id: "coshh-fuel-spill",
        label: "COSHH/fuel/spill station",
        category: "COSHH, asbestos & environment",
        summary: "COSHH store, fuel store, spill kit, waste point and containment barrier.",
        tags: ["COSHH", "fuel", "spill", "storage", "waste", "environment"],
        elements: [
          { kind: "zone", zoneType: "laydown", x: -104, y: -58, w: 208, h: 116, label: "COSHH / fuel storage" },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -122, y: 78 }, { x: 122, y: 78 }] },
          { kind: "icon", icon: "coshh", x: -62, y: -10 },
          { kind: "icon", icon: "fuel", x: -10, y: -10 },
          { kind: "icon", icon: "spill", x: 42, y: -10 },
          { kind: "icon", icon: "waste", x: 86, y: 34 },
          { kind: "label", x: -104, y: 108, text: "COSHH/fuel store - spill kit and waste controls", fontSize: 13 },
        ],
      },
      {
        id: "concrete-washout",
        label: "Concrete washout/environment",
        category: "COSHH, asbestos & environment",
        summary: "Concrete washout/containment area with water, spill kit and waste controls.",
        tags: ["concrete washout", "environment", "water", "spill", "waste"],
        elements: [
          { kind: "zone", zoneType: "washout", x: -98, y: -54, w: 196, h: 108, label: "Concrete washout / containment" },
          { kind: "icon", icon: "water", x: -48, y: -6 },
          { kind: "icon", icon: "spill", x: 12, y: -6 },
          { kind: "icon", icon: "waste", x: 68, y: -6 },
          { kind: "label", x: -96, y: 84, text: "Protect drains - contain washout and spills", fontSize: 13 },
        ],
      },
      {
        id: "asbestos-controlled-area",
        label: "Asbestos/hazard material area",
        category: "COSHH, asbestos & environment",
        summary: "Controlled exclusion area with asbestos marker, permit/hold point and waste route.",
        tags: ["asbestos", "hazardous material", "controlled area", "waste", "permit"],
        elements: [
          { kind: "zone", zoneType: "exclusion", x: -104, y: -58, w: 208, h: 116, label: "Controlled hazardous material area" },
          { kind: "route", routeType: "pedestrian", points: [{ x: -138, y: 82 }, { x: -44, y: 48 }, { x: 82, y: 34 }] },
          { kind: "icon", icon: "asbestos", x: -50, y: -8 },
          { kind: "icon", icon: "permit", x: 8, y: -8 },
          { kind: "icon", icon: "holdpoint", x: 66, y: -8 },
          { kind: "icon", icon: "waste", x: 98, y: 48 },
          { kind: "label", x: -110, y: 110, text: "Controlled area - stop work if suspect material found", fontSize: 13 },
        ],
      },
      {
        id: "noise-dust-interface",
        label: "Noise/dust interface controls",
        category: "COSHH, asbestos & environment",
        summary: "Noise/dust zone with public interface, monitoring/briefing marker and pedestrian diversion.",
        tags: ["noise", "dust", "public", "monitoring", "interface"],
        elements: [
          { kind: "zone", zoneType: "public", x: -132, y: -62, w: 264, h: 124, label: "Sensitive interface / neighbours" },
          { kind: "zone", zoneType: "exclusion", x: -64, y: -34, w: 128, h: 68, label: "Noise/dust workface" },
          { kind: "route", routeType: "pedestrian", points: [{ x: -160, y: 92 }, { x: 0, y: 92 }, { x: 160, y: 72 }] },
          { kind: "icon", icon: "noise", x: -24, y: 0 },
          { kind: "icon", icon: "cctv", x: 46, y: -6 },
          { kind: "icon", icon: "permitboard", x: 92, y: 44 },
          { kind: "label", x: -126, y: 124, text: "Manage noise/dust at occupied or public interface", fontSize: 13 },
        ],
      },
    ],
  },
  {
    heading: "Public interface & security",
    stencils: [
      {
        id: "public-interface-hoarding",
        label: "Public interface/hoarding",
        category: "Public interface & security",
        summary: "Public boundary, hoarding line, protected walkway, CCTV/security and controlled gate.",
        tags: ["public interface", "hoarding", "boundary", "walkway", "security"],
        elements: [
          { kind: "zone", zoneType: "public", x: -144, y: -78, w: 288, h: 156, label: "Public / occupied interface" },
          { kind: "barrier", barrierType: "boundary", points: [{ x: -164, y: -88 }, { x: 164, y: -88 }] },
          { kind: "barrier", barrierType: "walkway", points: [{ x: -152, y: 56 }, { x: 152, y: 56 }] },
          { kind: "route", routeType: "pedestrian", points: [{ x: -142, y: 56 }, { x: 142, y: 56 }] },
          { kind: "icon", icon: "gate", x: -126, y: -88 },
          { kind: "icon", icon: "cctv", x: 116, y: -88 },
          { kind: "icon", icon: "crossing", x: 0, y: 56 },
          { kind: "label", x: -140, y: 106, text: "Public protected route and site boundary controls", fontSize: 13 },
        ],
      },
      {
        id: "occupied-premises-access",
        label: "Occupied-premises access",
        category: "Public interface & security",
        summary: "Visitor/tenant route through an occupied site with crossing, gate and segregation.",
        tags: ["occupied premises", "residents", "visitors", "customer route", "segregation"],
        elements: [
          { kind: "zone", zoneType: "public", x: -116, y: -58, w: 232, h: 116, label: "Occupied / live area" },
          { kind: "route", routeType: "pedestrian", points: [{ x: -164, y: 42 }, { x: -50, y: 10 }, { x: 146, y: 10 }] },
          { kind: "barrier", barrierType: "barrier", points: [{ x: -126, y: -72 }, { x: 126, y: -72 }] },
          { kind: "icon", icon: "gate", x: -170, y: 42 },
          { kind: "icon", icon: "crossing", x: -50, y: 10 },
          { kind: "icon", icon: "cctv", x: 110, y: -72 },
          { kind: "label", x: -114, y: 88, text: "Occupied area - keep visitors away from workface", fontSize: 13 },
        ],
      },
      {
        id: "site-security-out-of-hours",
        label: "Site security/out-of-hours",
        category: "Public interface & security",
        summary: "Boundary, gate, CCTV, stop/no-entry point and public exclusion marker.",
        tags: ["security", "out of hours", "boundary", "unauthorised access", "CCTV"],
        elements: [
          { kind: "barrier", barrierType: "boundary", points: [{ x: -150, y: -64 }, { x: 150, y: -64 }, { x: 150, y: 64 }, { x: -150, y: 64 }, { x: -150, y: -64 }] },
          { kind: "icon", icon: "gate", x: -150, y: 0 },
          { kind: "icon", icon: "cctv", x: 118, y: -64 },
          { kind: "icon", icon: "stop", x: -98, y: 0 },
          { kind: "icon", icon: "approval", x: 28, y: 0 },
          { kind: "label", x: -130, y: 96, text: "Secure boundary - no unauthorised access", fontSize: 13 },
        ],
      },
    ],
  },
];

export const COMPOSITE_STENCILS = COMPOSITE_STENCIL_GROUPS.flatMap((group) => group.stencils);

export function findCompositeStencil(id: string): CompositeStencil | null {
  return COMPOSITE_STENCILS.find((stencil) => stencil.id === id) ?? null;
}

function anchorPoint(anchor: StencilPoint, p: StencilPoint): StencilPoint {
  return { x: anchor.x + p.x, y: anchor.y + p.y };
}

/** Converts a composite RAMS stencil into real plan elements at the clicked anchor point. */
export function instantiateCompositeStencil(stencil: CompositeStencil, anchor: StencilPoint): PlanElement[] {
  return stencil.elements.map((template): PlanElement => {
    if (template.kind === "zone") {
      return {
        id: allocElId(),
        kind: "zone",
        zoneType: template.zoneType,
        x: anchor.x + template.x,
        y: anchor.y + template.y,
        w: template.w,
        h: template.h,
        label: template.label ?? "",
        color: template.color,
      };
    }
    if (template.kind === "route") {
      return {
        id: allocElId(),
        kind: "route",
        routeType: template.routeType,
        points: template.points.map((point) => anchorPoint(anchor, point)),
        color: template.color,
      };
    }
    if (template.kind === "barrier") {
      return {
        id: allocElId(),
        kind: "barrier",
        barrierType: template.barrierType,
        points: template.points.map((point) => anchorPoint(anchor, point)),
      };
    }
    if (template.kind === "icon") {
      return {
        id: allocElId(),
        kind: "icon",
        icon: template.icon,
        x: anchor.x + template.x,
        y: anchor.y + template.y,
        radius: template.radius,
        size: template.size,
        color: template.color,
      };
    }
    return {
      id: allocElId(),
      kind: "label",
      x: anchor.x + template.x,
      y: anchor.y + template.y,
      text: template.text,
      color: template.color,
      fontSize: template.fontSize,
    };
  });
}

/** Colour swatches offered in the selection panel. */
export const ELEMENT_COLORS = [
  "#0f172a",
  "#475569",
  "#dc2626",
  "#f2772e",
  "#8a6400",
  "#1ba37b",
  "#0e7a5c",
  "#0284c7",
  "#2563eb",
  "#7c3aed",
] as const;

/* ── Scale & measurement helpers ─────────────────────────────────────────── */

export function findCalibration(els: PlanElement[]): CalibrationEl | null {
  return (els.find((e) => e.kind === "calibration") as CalibrationEl | undefined) ?? null;
}

/** Metres per canvas unit, or null while uncalibrated. */
export function metresPerUnit(els: PlanElement[]): number | null {
  const cal = findCalibration(els);
  if (!cal || cal.metres <= 0) return null;
  const len = Math.hypot(cal.x2 - cal.x1, cal.y2 - cal.y1);
  return len > 4 ? cal.metres / len : null;
}

export function formatMetres(units: number, mpu: number | null): string | null {
  if (mpu === null) return null;
  const m = units * mpu;
  return m >= 100 ? `${Math.round(m)} m` : `${m.toFixed(1)} m`;
}

/* ── Geometry helpers ────────────────────────────────────────────────────── */

export function distToSegment(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  return Math.hypot(p.x - cx, p.y - cy);
}

function distToPolyline(p: { x: number; y: number }, pts: { x: number; y: number }[]): number {
  let min = Infinity;
  for (let i = 0; i < pts.length - 1; i++) min = Math.min(min, distToSegment(p, pts[i], pts[i + 1]));
  return pts.length === 1 ? Math.hypot(p.x - pts[0].x, p.y - pts[0].y) : min;
}

/** Rotates a point around a centre by -deg (into the element's local space). */
export function toLocal(p: { x: number; y: number }, cx: number, cy: number, deg: number | undefined) {
  if (!deg) return p;
  const rad = (-deg * Math.PI) / 180;
  const dx = p.x - cx;
  const dy = p.y - cy;
  return { x: cx + dx * Math.cos(rad) - dy * Math.sin(rad), y: cy + dx * Math.sin(rad) + dy * Math.cos(rad) };
}

function inBox(p: { x: number; y: number }, el: { x: number; y: number; w: number; h: number; rotation?: number }) {
  const local = toLocal(p, el.x + el.w / 2, el.y + el.h / 2, el.rotation);
  return local.x >= el.x && local.x <= el.x + el.w && local.y >= el.y && local.y <= el.y + el.h;
}

/** Evenly spaced points (with tangent angle) along a polyline — used for fence posts / barrier ticks. */
export function pointsAlongPolyline(
  pts: { x: number; y: number }[],
  spacing: number,
): { x: number; y: number; angle: number }[] {
  const out: { x: number; y: number; angle: number }[] = [];
  let carried = spacing / 2;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len === 0) continue;
    const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
    let d = carried;
    while (d <= len) {
      const t = d / len;
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, angle });
      d += spacing;
    }
    carried = d - len;
  }
  return out;
}

/** Point at half the total length of a polyline — used for service-run letter badges. */
export function polylineMidpoint(pts: { x: number; y: number }[]): { x: number; y: number } {
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) total += Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
  let d = total / 2;
  for (let i = 0; i < pts.length - 1; i++) {
    const len = Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y);
    if (d <= len && len > 0) {
      const t = d / len;
      return { x: pts[i].x + (pts[i + 1].x - pts[i].x) * t, y: pts[i].y + (pts[i + 1].y - pts[i].y) * t };
    }
    d -= len;
  }
  return pts[0] ?? { x: 0, y: 0 };
}

export const LEGEND_ROW = 16;
export const LEGEND_WIDTH = 190;

/** Entries the on-canvas legend will show — also used for its hit box. */
export function legendEntries(els: PlanElement[]): { swatchKind: "zone" | "route" | "barrier" | "icon"; key: string; label: string }[] {
  const entries: { swatchKind: "zone" | "route" | "barrier" | "icon"; key: string; label: string }[] = [];
  (Object.keys(ZONE_STYLE) as ZoneType[]).forEach((zt) => {
    if (els.some((e) => e.kind === "zone" && e.zoneType === zt)) entries.push({ swatchKind: "zone", key: zt, label: ZONE_STYLE[zt].label });
  });
  (Object.keys(ROUTE_STYLE) as RouteType[]).forEach((rt) => {
    if (els.some((e) => e.kind === "route" && e.routeType === rt)) entries.push({ swatchKind: "route", key: rt, label: ROUTE_STYLE[rt].label });
  });
  (Object.keys(BARRIER_STYLE) as BarrierType[]).forEach((bt) => {
    if (els.some((e) => e.kind === "barrier" && e.barrierType === bt)) entries.push({ swatchKind: "barrier", key: bt, label: BARRIER_STYLE[bt].label });
  });
  (Object.keys(ICON_META) as IconType[]).forEach((ic) => {
    if (els.some((e) => e.kind === "icon" && e.icon === ic)) entries.push({ swatchKind: "icon", key: ic, label: ICON_META[ic].label });
  });
  return entries;
}

export function hitTest(els: PlanElement[], p: { x: number; y: number }): PlanElement | null {
  // Top-most first, mirroring render order: legend, labels/icons, measures,
  // lines, draw strokes, routes, shapes, images, zones.
  const reversed = [...els].reverse();
  for (const el of reversed) {
    if (el.kind === "legend") {
      const rows = legendEntries(els).length;
      if (p.x >= el.x && p.x <= el.x + LEGEND_WIDTH && p.y >= el.y && p.y <= el.y + 26 + rows * LEGEND_ROW) return el;
    }
  }
  for (const el of reversed) {
    if (el.kind === "icon" && Math.hypot(p.x - el.x, p.y - el.y) <= 16 * (el.size ?? 1)) return el;
    if (el.kind === "label") {
      const fs = el.fontSize ?? 14;
      const w = el.text.length * fs * 0.52 + 8;
      if (p.y >= el.y - fs && p.y <= el.y + 4 && p.x >= el.x - 4 && p.x <= el.x + w) return el;
    }
  }
  for (const el of reversed) {
    if (el.kind === "measure" || el.kind === "calibration") {
      if (distToSegment(p, { x: el.x1, y: el.y1 }, { x: el.x2, y: el.y2 }) <= 8) return el;
    }
    if (el.kind === "line" && distToSegment(p, { x: el.x1, y: el.y1 }, { x: el.x2, y: el.y2 }) <= Math.max(8, el.width)) return el;
    if (el.kind === "draw" && distToPolyline(p, el.points) <= Math.max(6, el.width / 2 + 3)) return el;
  }
  for (const el of reversed) {
    if (el.kind === "route" || el.kind === "barrier") {
      const tol = el.kind === "barrier" ? Math.max(10, BARRIER_STYLE[el.barrierType].width) : 10;
      for (let i = 0; i < el.points.length - 1; i++) {
        if (distToSegment(p, el.points[i], el.points[i + 1]) <= tol) return el;
      }
    }
  }
  for (const el of reversed) {
    if (el.kind === "shape" && inBox(p, el)) return el;
  }
  for (const el of reversed) {
    if (el.kind === "image" && inBox(p, el)) return el;
  }
  for (const el of reversed) {
    if (el.kind === "zone" && inBox(p, el)) return el;
  }
  return null;
}

/** Moves any element by a delta, returning a new element. */
export function moveElement(el: PlanElement, dx: number, dy: number): PlanElement {
  switch (el.kind) {
    case "zone":
    case "shape":
    case "image":
    case "icon":
    case "label":
    case "legend":
      return { ...el, x: el.x + dx, y: el.y + dy };
    case "route":
    case "barrier":
    case "draw":
      return { ...el, points: el.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy })) };
    case "line":
    case "measure":
    case "calibration":
      return { ...el, x1: el.x1 + dx, y1: el.y1 + dy, x2: el.x2 + dx, y2: el.y2 + dy };
  }
}

/* ── Image helpers ───────────────────────────────────────────────────────── */

function readImageFile(file: File, maxEdge: number, quality: number): Promise<{ dataUrl: string; width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(null);
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve({ dataUrl: canvas.toDataURL("image/jpeg", quality), width: canvas.width, height: canvas.height });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  });
}

export async function imageFileToBackground(file: File): Promise<PlanBackground | null> {
  const img = await readImageFile(file, 1800, 0.86);
  return img ? { dataUrl: img.dataUrl, name: file.name, opacity: 0.42 } : null;
}

/** Builds an inserted-image element sized to fit comfortably on the canvas. */
export async function imageFileToElement(file: File): Promise<ImageEl | null> {
  const img = await readImageFile(file, 1000, 0.82);
  if (!img) return null;
  const maxW = 280;
  const maxH = 220;
  const scale = Math.min(maxW / img.width, maxH / img.height, 1);
  const w = Math.max(24, Math.round(img.width * scale));
  const h = Math.max(24, Math.round(img.height * scale));
  return {
    id: allocElId(),
    kind: "image",
    x: Math.round((W - w) / 2),
    y: Math.round((H - h) / 2),
    w,
    h,
    dataUrl: img.dataUrl,
    name: file.name,
  };
}
